import { autoPlacement, autoUpdate, computePosition, flip, offset, shift, size, type Placement } from '@floating-ui/dom'
import { animationFrameScheduler, auditTime, EMPTY, merge, Observable } from 'rxjs'
import { fromResizeObserver } from '@schmancy/directives/layout'
import { ANCHOR_FIT_PADDING_PX, type ResolvedAnchor } from './overlay.layout'
import { layoutViewportPlatform } from './overlay.soft-keyboard'
import type { OverlayPlacement, OverlayTier } from './overlay.types'

/**
 * Three-tier positioning ladder for anchored overlays. Public API of the
 * overlay doesn't change based on the tier — the strategy is picked
 * internally by feature-detection.
 *
 * Tier 1 — CSS Anchor Positioning (Popover + `anchor-name` / `position-anchor`)
 *   Zero-JS position tracking. Chromium-only today.
 *
 * Tier 2 — Popover API + Floating UI
 *   Native top-layer from `popover="manual"` — NOT `"auto"`, so no native
 *   light-dismiss and no native Esc: schmancy owns both (`wireCloseTriggers`),
 *   which is what lets `sticky` and `dismissable` mean anything at all. Floating
 *   UI middleware for the position math. Safari 17+, Chrome 114+, Firefox 125+.
 *
 * Tier 3 — Floating UI only
 *   Fallback for browsers without the Popover API. Caller provides manual
 *   backdrop + click-outside + Esc wiring.
 */

/** The fraction of its frame a grown panel may take. The same 90% the bounded
 *  sheet takes (`:host([bounded]) .surface[data-tier='modal']`), which is itself
 *  `max-h-[90svh]` restated against the frame — so a panel drawn in a phone frame
 *  leaves the same strip of ground the same panel leaves on the phone. */
const GROW_MAX_FRAME_FRACTION = 0.9

/**
 * Feature-detection snapshot captured at module load. Frozen so consumers
 * can safely destructure and compare.
 */
export const CAPS = Object.freeze({
	popover:
		typeof HTMLElement !== 'undefined' &&
		'popover' in HTMLElement.prototype &&
		typeof (HTMLElement.prototype as unknown as { showPopover?: () => void }).showPopover === 'function',
	cssAnchor: typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('anchor-name: --x'),
})

/**
 * Pick the positioning tier for an anchor.
 *
 * `surfaceRoot` — the node tree the positioned surface lives in — is required
 * rather than optional because a caller allowed to omit it re-opens the silent
 * mis-anchor below.
 *
 * CSS Anchor Positioning (Tier 1) requires a real element anchor IN THE SAME
 * NODE TREE as the surface. `anchor-name` is a tree-scoped name and
 * `position-anchor` a tree-scoped reference: the lookup happens in the tree the
 * reference was specified in and does not cross a shadow boundary in either
 * direction — measured in Chromium, an anchor one tree away resolves to no
 * anchor at all, `anchor()` goes invalid-at-computed-value-time, and the surface
 * silently falls back to its static position with no error anywhere. So the
 * anchor's root node has to BE the surface's root, not merely reachable from it.
 * A cross-tree element anchor takes Tier 2, whose Floating UI math reads
 * `getBoundingClientRect()` and is indifferent to which tree the anchor sits in.
 *
 * `<schmancy-overlay>` renders its surface inside its own shadow root, so an
 * anchor it was handed is never in that tree and it always lands on Tier 2. The
 * tier stays reachable for a caller that mounts its surface into the anchor's
 * own root — the shape `tooltip.directive.ts` already uses for exactly this
 * reason.
 *
 * A point/rect anchor also forces Tier 2 (or Tier 3 if popover isn't supported):
 * you can't point `anchor-name` at a DOMRect or a coord.
 *
 * A `boundary` forces Tier 2 as well: Tier 1's fitting is `position-try-fallbacks`,
 * which the browser evaluates against the viewport with no way to name a smaller
 * box, so an overlay scoped to a frame would flip and clamp against the window
 * on this tier alone — the one tier that cannot honour the boundary the other
 * two do.
 */
export function pickPositioner(anchor: ResolvedAnchor, surfaceRoot: Node, boundary?: Element): OverlayTier {
	if (CAPS.popover && CAPS.cssAnchor && anchor.el?.getRootNode() === surfaceRoot && !boundary) return 'css-anchor'
	if (CAPS.popover) return 'popover-fui'
	return 'fui-only'
}

/* ======================================================================= *
 *                   Tier 1 — Popover API + CSS Anchor                      *
 * ======================================================================= */

/**
 * Set up CSS Anchor Positioning. Assigns a unique `anchor-name` to the
 * anchor element and injects a stylesheet into the shadow root that
 * positions the surface relative to it. No JS running per frame.
 *
 * Returns a cleanup function that removes the injected styles and the
 * anchor-name from the anchor element.
 */
export function positionCSSAnchor(
	surface: HTMLElement,
	anchor: ResolvedAnchor,
	shadowRoot: ShadowRoot,
	opts: { id: string; placement?: OverlayPlacement } = { id: '' },
): () => void {
	if (!anchor.el) {
		throw new Error('positionCSSAnchor requires an element anchor')
	}
	const anchorEl = anchor.el
	if (import.meta.env.DEV && anchorEl.getRootNode() !== surface.getRootNode()) {
		console.warn(
			'[schmancy-overlay] positionCSSAnchor was given an anchor in a different node tree from the ' +
				'surface. `anchor-name` is tree-scoped and does not cross a shadow boundary, so the ' +
				'`anchor()` reference will not resolve and the surface will sit at its static position ' +
				'with no further error. Route this pair through the Floating UI tier (`pickPositioner` ' +
				"already does), or mount the surface into the anchor's own root.",
			anchorEl,
		)
	}
	const anchorName = `--ov-${opts.id || Math.random().toString(36).slice(2, 10)}`
	const placement = opts.placement ?? 'bottom-start'

	// Save any prior anchor-name so we can restore it on cleanup.
	const priorAnchorName = anchorEl.style.getPropertyValue('anchor-name')
	anchorEl.style.setProperty('anchor-name', anchorName)

	const sheet = new CSSStyleSheet()
	sheet.replaceSync(`
		${surface.localName}[data-overlay-ref="${opts.id}"] {
			position-anchor: ${anchorName};
			${cssForPlacement(placement)}
			margin: 8px;
			position-try-fallbacks:
				flip-block,
				flip-inline,
				flip-block flip-inline;
		}
	`)
	surface.dataset.overlayRef = opts.id
	const existing = shadowRoot.adoptedStyleSheets
	shadowRoot.adoptedStyleSheets = [...existing, sheet]

	return () => {
		if (priorAnchorName) {
			anchorEl.style.setProperty('anchor-name', priorAnchorName)
		} else {
			anchorEl.style.removeProperty('anchor-name')
		}
		shadowRoot.adoptedStyleSheets = shadowRoot.adoptedStyleSheets.filter(s => s !== sheet)
		delete surface.dataset.overlayRef
	}
}

function cssForPlacement(p: OverlayPlacement): string {
	// Minimal mapping for the Tier 1 stylesheet. The browser's
	// `position-try-fallbacks` flips when the preferred side doesn't fit,
	// so only the starting side needs to be encoded here.
	switch (p) {
		case 'top':
		case 'top-start':
			return 'bottom: anchor(top); left: anchor(start);'
		case 'top-end':
			return 'bottom: anchor(top); right: anchor(end);'
		case 'bottom':
		case 'bottom-start':
			return 'top: anchor(bottom); left: anchor(start);'
		case 'bottom-end':
			return 'top: anchor(bottom); right: anchor(end);'
		case 'left':
		case 'left-start':
			return 'right: anchor(left); top: anchor(start);'
		case 'left-end':
			return 'right: anchor(left); bottom: anchor(end);'
		case 'right':
		case 'right-start':
			return 'left: anchor(right); top: anchor(start);'
		case 'right-end':
			return 'left: anchor(right); bottom: anchor(end);'
	}
}

/* ======================================================================= *
 *                   Tier 2 — Popover API (top-layer only)                   *
 * ======================================================================= */

/**
 * Wire the Popover API lifecycle — the surface gets `popover="manual"` and
 * `showPopover()` is called to promote it into the native top layer. No
 * position math here; the caller pairs this with `positionFloatingUI` for
 * the geometry, since Tier 2 is Popover-for-stacking + Floating UI-for-math.
 *
 * `manual` (not `auto`) so stacked overlays don't fight the browser's
 * popover-auto light-dismiss algorithm: opening a second `popover="auto"`
 * that isn't a DOM/invoker descendant of the first closes the first via
 * the toggle event. Each schmancy overlay is appended as a sibling to
 * `<body>`, so the browser can't see the nesting and would dismiss the
 * parent. With `manual`, schmancy owns Esc + outside-click via
 * `wireCloseTriggers`, and the top-layer rendering is preserved.
 *
 * Returns a cleanup function that hides the popover and strips the attr.
 */
export function positionPopoverAPI(surface: HTMLElement): () => void {
	if (!CAPS.popover) {
		throw new Error('positionPopoverAPI requires Popover API support')
	}
	surface.setAttribute('popover', 'manual')
	try {
		;(surface as unknown as { showPopover(): void }).showPopover()
	} catch {
		// showPopover can throw if already shown or detached; the component
		// is responsible for calling this on an already-mounted, not-yet-shown
		// surface. Silently swallowing a double-open is fine.
	}
	return () => {
		try {
			;(surface as unknown as { hidePopover?: () => void }).hidePopover?.()
		} catch {
			// already hidden / disconnected — fine.
		}
		surface.removeAttribute('popover')
	}
}

/* ======================================================================= *
 *                  Tier 2 / 3 — Floating UI position math                   *
 * ======================================================================= */

export interface FloatingUIOptions {
	/** Preferred placement; Floating UI's `flip` middleware handles falls. */
	placement?: OverlayPlacement
	/** Padding used by `shift` + `size` middleware. Defaults to
	 *  `ANCHOR_FIT_PADDING_PX` from overlay.layout. */
	padding?: number
	/** Gap between anchor and surface. Default 8px. */
	offsetPx?: number
	/** Whether the surface re-positions when it or its anchor's element resizes. Default true. */
	track?: boolean
	/** Poll position every animation frame instead of reacting to
	 *  scroll/resize observers. Tracking otherwise re-anchors on ancestor
	 *  scroll and ancestor resize (Floating UI's `autoUpdate`) and on the
	 *  reference element's own resize (this module's ResizeObserver) — none of
	 *  which fire when a reference element's position
	 *  changes via a direct `style.left`/`style.top` write with no size or
	 *  scroll change involved (e.g. a `pin`-draggable anchor). `animationFrame`
	 *  trades that event-driven efficiency for per-frame `getBoundingClientRect`
	 *  polling, so it is opt-in per call rather than a package-wide default —
	 *  every other anchored overlay (menus, dropdowns, tooltips) keeps the
	 *  cheap scroll/resize-driven path. Default false. */
	animationFrame?: boolean
	/** Clipping box every fitting decision is measured against — the flip's
	 *  available sides, the shift's clamp, the size's `availableWidth`/
	 *  `availableHeight`. Absent, Floating UI's own default (`clippingAncestors`,
	 *  which bottoms out at the layout viewport — see `layoutViewportPlatform`)
	 *  decides. Pass the frame element to keep
	 *  an anchored surface inside a box smaller than the window. */
	boundary?: Element
	/** Opt-in smart-grow for a POINT (click) anchor. When true, the decider is
	 *  `autoPlacement` (grows into the roomiest vertical region, up or down) and
	 *  the first pass measures natural content. When false/absent the decider is
	 *  `flip` — the trigger-respecting default every menu/dropdown relies on.
	 *  Element/rect anchors ignore this (always `flip`). */
	grow?: boolean
	/** Expand over the source corner, shifting onto the viewport without choosing a side. */
	coverSource?: boolean
}

/**
 * Floating UI-driven positioning. Returns a cold Observable — subscribe to
 * start positioning, unsubscribe to stop tracking (teardown cancels the
 * `autoUpdate` loop). The caller composes with `takeUntil(this.disconnecting)`:
 *
 *     positionFloatingUI(surface, anchor, opts)
 *       .pipe(takeUntil(this.disconnecting))
 *       .subscribe()
 *
 * Two modes — default (anchored, trigger-tucked) and opt-in grow (fills the viewport):
 *
 *   NON-GROW — every menu, dropdown, date picker, element/rect anchor:
 *     offset → flip({ fallbackPlacements: defaultFallbacks(placement) })
 *            → shift({ padding })  (main axis only)
 *            → size({ apply: clamp maxWidth/maxHeight to the ONE-SIDED availableWidth/Height
 *                     between the anchor and the nearest viewport edge })
 *     The surface stays tucked to its trigger side and scrolls internally only if it is
 *     huge.
 *
 *   GROW — point anchor + opts.grow === true (a form/dialog that asked to grow):
 *     offset → autoPlacement({ crossAxis: true, allowedPlacements: pointPlacements(placement) })
 *            → shift({ padding, crossAxis: true })  (BOTH axes)
 *     NO size() clamp. The surface sizes to its NATURAL content, bounded only by the CSS
 *     viewport cap (max-h-[90dvh] / max-w-[calc(100vw-2rem)]); both-axis shift then slides the
 *     whole panel fully on-screen — so it expands from the click UPWARD / outward to use the
 *     available viewport, and the surface's overflow-auto scrolls ONLY when the content truly
 *     exceeds the viewport. Dropping size() is the point: its one-sided availableHeight clamp
 *     pinned the panel beside the anchor and forced the inner scroll even with room left on the
 *     page. With no inline max written, nothing re-inflates per tick → the floating-element
 *     ResizeObserver settles (no oscillation). Opt-in, so menus/date-range keep the NON-GROW path.
 *
 *   GROW inside a boundary — the same promise, kept against the frame:
 *     the CSS cap that gives an unbounded grow panel its room measures the window, and a
 *     bounded anchored surface cannot reach its frame from CSS at all (it is `fixed` in the
 *     top layer, outside every ancestor box). So size() stays on here — but writes the FRAME's
 *     own box rather than its own one-sided availableWidth/Height, which measures the gap from
 *     the trigger to one frame edge and shrinks as the trigger moves toward the middle of a
 *     list. Both-axis shift then slides the panel inside the frame exactly as it slides an
 *     unbounded one onto the screen.
 */
export function positionFloatingUI(
	surface: HTMLElement,
	anchor: ResolvedAnchor,
	opts: FloatingUIOptions = {},
): Observable<void> {
	return new Observable<void>(subscriber => {
		const reference = anchor.el ?? anchor.virtual
		if (!reference) {
			subscriber.error(new Error('positionFloatingUI requires an element or virtual anchor'))
			return
		}

		const placement: Placement = (opts.placement ?? 'bottom-start') as Placement
		const padding = opts.padding ?? ANCHOR_FIT_PADDING_PX
		const offsetPx = opts.offsetPx ?? 8
		// Spread into every fitting middleware: `{}` when unbounded leaves each one
		// on Floating UI's own `clippingAncestors` default, byte-identical to an
		// overlay that never heard of a boundary.
		const clip = opts.boundary ? { boundary: opts.boundary } : {}

		// Smart-grow is opt-in and point-only — see JSDoc above for the GROW vs
		// NON-GROW split.
		const useGrow = anchor.kind === 'point' && opts.grow === true
		// Decider built once per positioning session — not per tick.
		const decider = useGrow
			? autoPlacement({ padding, crossAxis: true, allowedPlacements: pointPlacements(placement), ...clip })
			: flip({ padding, fallbackPlacements: defaultFallbacks(placement), ...clip })

		// Non-grow: flip + main-axis shift + size() clamp (tucked to trigger side).
		// Grow: autoPlacement + both-axis shift, no size() clamp (natural content size)
		// — except inside a boundary, where the frame IS the size the surface may
		// take, so the clamp stays on and the panel grows to the frame instead of
		// the window it cannot see.
		const middleware = opts.coverSource
			? [shift({ padding, crossAxis: true, ...clip })]
			: useGrow && !opts.boundary
				? [offset(offsetPx), decider, shift({ padding, crossAxis: true })]
				: [
						offset(offsetPx),
						decider,
						shift({ padding, crossAxis: useGrow, ...clip }),
						size({
							padding,
							...clip,
							apply({ availableWidth, availableHeight, elements }) {
								// A grow panel inside a frame is capped by the frame, not by the
								// gap between the trigger and one of its edges — see the GROW
								// inside a boundary note above. Read live, so a frame that
								// resizes re-caps on the next autoUpdate tick; and a cap read
								// off the frame is a fixed point (writing it cannot change the
								// frame), so it carries none of the per-tick re-inflation the
								// unbounded grow path dropped size() to escape.
								const frame = useGrow ? opts.boundary?.getBoundingClientRect() : undefined
								const maxWidth = frame ? frame.width - padding * 2 : availableWidth
								// Whichever is tighter: the 90% share, or what the frame has left
								// once the fit padding is taken off both edges. On a frame short
								// enough for those to cross, the share alone would leave a panel
								// shift() cannot slide back inside — and this surface is in the
								// top layer, where nothing clips the spill.
								const maxHeight = frame
									? Math.min(frame.height * GROW_MAX_FRAME_FRACTION, frame.height - padding * 2)
									: availableHeight
								elements.floating.style.maxWidth = `${Math.max(0, maxWidth)}px`
								elements.floating.style.maxHeight = `${Math.max(0, maxHeight)}px`
							},
						}),
					]

		const update = async (): Promise<void> => {
			try {
				const { x, y } = await computePosition(reference, surface, {
					strategy: 'fixed',
					placement,
					middleware,
					platform: layoutViewportPlatform,
				})
				Object.assign(surface.style, {
					position: 'fixed',
					left: `${Math.round(x)}px`,
					top: `${Math.round(y)}px`,
					transform: 'none',
				})
				subscriber.next()
			} catch (err) {
				subscriber.error(err)
			}
		}

		// A size() write made while the browser delivers the surface's own resize
		// notification changes a box it already reported, and the notification owed
		// for that change is dropped ("ResizeObserver loop completed with undelivered
		// notifications") — whenever the clamp bites, as it does for any panel wider
		// than the room beside a phone-width trigger. Element resizes therefore
		// re-position on the next frame, ahead of that frame's observations.
		// autoUpdate keeps scroll, window resize and layout shift, none of which
		// arrives mid-delivery.
		const cleanup = autoUpdate(reference, surface, update, {
			ancestorScroll: true,
			ancestorResize: true,
			elementResize: false,
			animationFrame: opts.animationFrame === true,
		})

		// The targets autoUpdate observed: the surface, and the reference's element
		// — a virtual anchor's `contextElement` — except under the per-frame poll,
		// which already follows the anchor.
		const referenceEl = reference instanceof Element ? reference : reference.contextElement
		const resized = (
			opts.track === false
				? EMPTY
				: merge(
						fromResizeObserver(surface),
						referenceEl && opts.animationFrame !== true ? fromResizeObserver(referenceEl) : EMPTY,
					)
		)
			.pipe(auditTime(0, animationFrameScheduler))
			.subscribe(() => void update())

		return () => {
			cleanup()
			resized.unsubscribe()
		}
	})
}

/**
 * Vertical-only placement set for point anchors (zero-size click coords).
 * No left/right — point overlays grow up or down into the roomiest region.
 * Preferred side listed FIRST so autoPlacement's stable sort returns it for
 * short content that fits → deterministic settle, removes the menu side-jump.
 */
function pointPlacements(preferred: Placement): Placement[] {
	switch (preferred) {
		case 'bottom-start':
		case 'top-start':
			return ['bottom-start', 'top-start', 'bottom-end', 'top-end']
		case 'bottom-end':
		case 'top-end':
			return ['bottom-end', 'top-end', 'bottom-start', 'top-start']
		default:
			// Any left/right preferred — no app caller passes these; kept for type-totality.
			return ['bottom-start', 'top-start', 'bottom-end', 'top-end']
	}
}

function defaultFallbacks(preferred: Placement): Placement[] {
	// Flip to the opposite side first, then try the perpendicular ends.
	// Small, deterministic list — not exhaustive.
	switch (preferred) {
		case 'bottom-start':
			return ['top-start', 'bottom-end', 'top-end']
		case 'bottom-end':
			return ['top-end', 'bottom-start', 'top-start']
		case 'top-start':
			return ['bottom-start', 'top-end', 'bottom-end']
		case 'top-end':
			return ['bottom-end', 'top-start', 'bottom-start']
		case 'left-start':
			return ['right-start', 'left-end', 'right-end']
		case 'left-end':
			return ['right-end', 'left-start', 'right-start']
		case 'right-start':
			return ['left-start', 'right-end', 'left-end']
		case 'right-end':
			return ['left-end', 'right-start', 'left-start']
		case 'top':
			return ['bottom', 'left', 'right']
		case 'bottom':
			return ['top', 'left', 'right']
		case 'left':
			return ['right', 'top', 'bottom']
		case 'right':
			return ['left', 'top', 'bottom']
		default:
			return ['top-start', 'bottom-end']
	}
}
